import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaFacebook, FaCheckCircle, FaUsers, FaHeart, FaShare, FaEye, FaClock, FaShieldAlt } from 'react-icons/fa';

const FacebookPackages = ({ addToCart }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);

  const packages = [
    {
      id: 'fb-standard',
      name: 'Standard',
      followers: 500,
      likes: 250,
      price: 9.99,
      deliveryTime: '2-3 days',
      features: [
        '500 Real Facebook Followers',
        '250 Post Likes',
        'Fast Delivery',
        '24/7 Support',
        '30-Day Guarantee',
        'No Password Required'
      ],
      popular: false
    },
    {
      id: 'fb-medium',
      name: 'Medium',
      followers: 1000,
      likes: 500,
      price: 19.99,
      deliveryTime: '1-2 days',
      features: [
        '1,000 Real Facebook Followers',
        '500 Post Likes',
        'Priority Delivery',
        '24/7 Support',
        '30-Day Guarantee',
        'No Password Required'
      ],
      popular: true
    },
    {
      id: 'fb-pro',
      name: 'Pro',
      followers: 2000,
      likes: 1000,
      price: 39.99,
      deliveryTime: '24 hours',
      features: [
        '2,000 Real Facebook Followers',
        '1,000 Post Likes',
        'Instant Delivery',
        'Dedicated Support',
        '30-Day Guarantee',
        'No Password Required'
      ],
      popular: false
    }
  ];

  const additionalServices = [
    {
      name: 'Facebook Page Likes',
      description: 'Get more likes on your Facebook posts',
      price: 'Starting from $4.99'
    },
    {
      name: 'Facebook Post Shares',
      description: 'Increase your post reach with shares',
      price: 'Starting from $9.99'
    },
    {
      name: 'Facebook Video Views',
      description: 'Boost your video views and engagement',
      price: 'Starting from $6.99'
    },
    {
      name: 'Facebook Comments',
      description: 'Get real comments on your posts',
      price: 'Starting from $7.99'
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="hero-gradient text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center">
                <FaFacebook className="text-4xl" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Facebook Growth Packages
            </h1>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              Boost your Facebook presence with real followers, likes, and engagement. Choose from our Standard, Medium, or Pro packages.
            </p>
          </div>
        </div>
      </section>

      {/* Package Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-purple-600 mb-4">
              Choose Your Package
            </h2>
            <p className="text-black text-lg">
              Select the perfect package for your Facebook growth needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {packages.map((pkg) => (
              <div key={pkg.id} className={`package-card ${pkg.popular ? 'featured' : ''}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-1 rounded-full text-xs font-bold">
                      POPULAR
                    </span>
                  </div>
                )}
                <div className="p-8 text-center">
                  <h3 className="text-2xl font-bold text-white mb-4">{pkg.name}</h3>
                  
                  <div className="mb-6">
                    <div className="text-5xl font-bold text-white mb-2">
                      {pkg.followers.toLocaleString()}
                    </div>
                    <div className="text-lg text-white">Followers</div>
                    <div className="text-sm text-purple-300">+ {pkg.likes.toLocaleString()} Likes</div>
                  </div>
                  
                  <div className="text-3xl font-bold text-white mb-8">
                    ${pkg.price}
                  </div>
                  
                  <div className="flex items-center justify-center text-white mb-6">
                    <FaClock className="mr-2" />
                    {pkg.deliveryTime}
                  </div>
                  
                  <ul className="space-y-3 mb-8 text-left">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-white">
                        <FaCheckCircle className="text-green-400 mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    onClick={() => {
                      setSelectedPackage(pkg);
                      if (addToCart) {
                        addToCart({
                          id: pkg.id,
                          name: `${pkg.name} Package`,
                          platform: 'Facebook',
                          price: pkg.price,
                          followers: pkg.followers
                        });
                      }
                    }}
                    className="btn-primary w-full"
                  >
                    Get Started
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Additional Facebook Services
            </h2>
            <p className="text-purple-600 text-lg">
              Enhance your Facebook presence with our additional services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices.map((service, index) => (
              <div key={index} className="card p-6 text-center hover:scale-105 transition-transform duration-300">
                <h3 className="text-lg font-semibold text-white mb-3">{service.name}</h3>
                <p className="text-white mb-4">{service.description}</p>
                <div className="text-white font-bold">{service.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
              Why Choose Our Facebook Services?
            </h2>
            <p className="text-gray-600 text-lg">
              We provide the best Facebook growth solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <FaUsers className="text-2xl" />,
                title: 'Real Followers',
                description: '100% real and active Facebook users'
              },
              {
                
                 icon: <FaShieldAlt className="text-2xl" />, 
                title: 'Safe & Secure',
                description: 'No password required, completely safe'
              },
              {
                icon: <FaEye className="text-2xl" />,
                title: 'Fast Delivery',
                description: 'Quick delivery within specified time'
              },
              {
                icon: <FaHeart className="text-2xl" />,
                title: 'Guaranteed Results',
                description: '30-day money-back guarantee'
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 mx-auto mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
              How It Works
            </h2>
            <p className="text-gray-600 text-lg">
              Simple 3-step process to grow your Facebook
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Choose Package</h3>
              <p className="text-gray-600">Select the package that fits your needs</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Enter Details</h3>
              <p className="text-gray-600">Provide your Facebook page URL</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">See Results</h3>
              <p className="text-gray-600">Watch your Facebook presence grow</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 hero-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Grow Your Facebook?
          </h2>
          <p className="text-xl mb-8 text-purple-100">
            Join thousands of satisfied customers who trust our Facebook services
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="btn-primary bg-white text-white hover:bg-purple-50">
                         Get Started Now
                       </Link>
                       <Link to="/contact" className="btn-secondary border-white text-white hover:bg-white hover:text-purple-600">
                         Contact Support
                       </Link>
          </div>
        </div>
      </section>

      {/* Package Selection Modal */}
      {selectedPackage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              {selectedPackage.name} Package Selected
            </h3>
            <div className="text-purple-600 font-bold text-3xl mb-6">
              ${selectedPackage.price}
            </div>
            <p className="text-gray-600 mb-6">
              You've selected the {selectedPackage.name} package with {selectedPackage.followers.toLocaleString()} followers.
            </p>
            <div className="flex gap-4">
              <button 
                onClick={() => setSelectedPackage(null)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <Link to="/signup" className="btn-primary flex-1 text-center">
                Continue
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacebookPackages;
